import { useQueries } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { trpc } from '@/api/trpc';
import { FormInput, FormSelect } from '@/components/UI';
import type { FuturesConfigurationDraft } from './futuresConfigurationDraft';

export default function FuturesConfigurationFields({
    value,
    onChange,
    strategyIds = [],
    allowedProductIds
}: {
    value: FuturesConfigurationDraft;
    strategyIds?: number[];
    allowedProductIds?: readonly number[];
    onChange: (value: FuturesConfigurationDraft) => void;
}) {
    const routeCatalog = useAppQuery(trpc.strategy.fetchFuturesSignalRouteCatalog.queryOptions());
    const routeQueries = useQueries({
        queries: strategyIds.map((strategyId) => trpc.strategy.fetchFuturesSignalRoutes.queryOptions({ strategyId }))
    });
    const routes = routeQueries.flatMap((query) => query.data ?? []);
    const selectedRoute = routes.find((route) => route.id === value.signalRouteId);
    const catalog = useAppQuery(trpc.backtest.getFuturesBacktestCatalog.queryOptions(), {
        fallbackErrorMessage: 'Failed to load futures research catalog',
        showErrorToast: true
    });
    const selected = catalog.data?.find((row) => row.productId === value.productId && row.assumptionId === value.assumptionId);
    return (
        <div className="grid gap-3 md:grid-cols-2">
            {routes.length > 0 && (
                <FormSelect
                    label="Signal route"
                    value={value.signalRouteId ?? ''}
                    options={[
                        { value: '', label: 'Select a saved ETF route…' },
                        ...routes.map((route) => {
                            const pair = routeCatalog.data?.find(
                                (pair) => pair.signalAssetId === route.signalAssetId && pair.productId === route.productId
                            );
                            return {
                                value: route.id,
                                label: `${pair ? `${pair.signalSymbol} → ${pair.productSymbol}` : `Route #${route.id}`} · strategy #${route.strategyId}`
                            };
                        })
                    ]}
                    onChange={(event) => {
                        const route = routes.find((route) => route.id === Number(event.target.value));
                        onChange({ ...value, signalRouteId: route?.id ?? null, productId: route?.productId ?? null, assumptionId: null });
                    }}
                />
            )}
            {routeQueries.some((query) => query.isError) && <p role="alert">Signal routes could not be loaded. Retry before submitting.</p>}
            <FormSelect
                label="Product and research costs"
                value={value.assumptionId ?? ''}
                options={[
                    { value: '', label: 'Select explicit research assumptions…' },
                    ...(catalog.data ?? [])
                        .filter(
                            (row) =>
                                (!routes.length || row.productId === selectedRoute?.productId) &&
                                (!allowedProductIds || allowedProductIds.includes(row.productId))
                        )
                        .map((row) => ({ value: row.assumptionId, label: `${row.symbol} — ${row.assumptionCode}` }))
                ]}
                onChange={(event) => {
                    const row = catalog.data?.find((item) => item.assumptionId === Number(event.target.value));
                    onChange({ ...value, productId: row?.productId ?? null, assumptionId: row?.assumptionId ?? null });
                }}
            />
            <FormInput
                label="Allocation per position (USD)"
                value={value.allocation}
                onChange={(event) => onChange({ ...value, allocation: event.target.value })}
                helpText="Determines the native contract count from the chosen per-contract research reserve."
            />
            <FormInput
                label="Warmup start (Eastern)"
                placeholder="2020-01-01 00:00:00"
                value={value.historyStart}
                onChange={(event) => onChange({ ...value, historyStart: event.target.value })}
                helpText="Must precede the first decision. Coverage is verified before submission."
            />
            <FormInput
                label="Close buffer (minutes)"
                type="number"
                min={1}
                max={1440}
                step={1}
                value={value.closeBufferMinutes}
                onChange={(event) => onChange({ ...value, closeBufferMinutes: event.target.value })}
            />
            {selected && (
                <p className="text-sm text-zinc-400 md:col-span-2">
                    Research assumptions: ${(Number(selected.feePerContractSideRaw) / 1e9).toFixed(2)} per contract per side,{' '}
                    {selected.slippageTicksPerSide} adverse ticks per side, $
                    {(Number(selected.capitalAllocationPerContractRaw) / 1e9).toLocaleString()} reserve per contract.{' '}
                    {selected.feeRationale} {selected.capitalRationale}
                </p>
            )}
        </div>
    );
}
